import { useRef, useState } from "react";
import { Send, MessageSquare, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { contentTypeLabel, contentRequestStatus, ITEM_STATE_META, frDate } from "@/lib/constants";
import { toast } from "sonner";

function uploadWithProgress(token, file, requestId, onProgress) {
  return new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("content_request_id", requestId);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${api.defaults.baseURL}/review/${token}/upload`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status < 300 ? resolve(JSON.parse(xhr.responseText)) : reject(new Error(`HTTP ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("network"));
    xhr.send(fd);
  });
}

function ItemDelivery({ token, request, item, onChanged }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [links, setLinks] = useState([]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState({});
  const [sending, setSending] = useState(false);
  const fileInput = useRef(null);
  const m = ITEM_STATE_META[item.state] || ITEM_STATE_META.en_attente;
  const isUploading = Object.keys(uploading).length > 0;

  const pickFiles = async (fileList) => {
    for (const f of Array.from(fileList)) {
      setUploading((u) => ({ ...u, [f.name]: 0 }));
      try {
        const res = await uploadWithProgress(token, f, request.id,
          (pct) => setUploading((u) => ({ ...u, [f.name]: pct })));
        setFiles((fl) => [...fl, { url: res.url, name: res.name }]);
      } catch {
        toast.error(`Échec de l'envoi de « ${f.name} » — vérifiez votre connexion et réessayez`);
      } finally {
        setUploading((u) => { const n = { ...u }; delete n[f.name]; return n; });
      }
    }
  };

  const submit = async () => {
    if (!text.trim() && !links.length && !files.length) { toast.error("Ajoutez du texte, un lien ou un fichier"); return; }
    if (isUploading) { toast.error("Envoi de fichier en cours — patientez avant de valider"); return; }
    setSending(true);
    try {
      await api.post(`/review/${token}/content-requests/${request.id}/items/${item.id}/deliver`,
        { text, links, files });
      toast.success("Contenu envoyé — merci !");
      setText(""); setLinks([]); setFiles([]); setOpen(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setSending(false); }
  };

  return (
    <div className="border border-neutral-200 rounded-lg p-3" data-testid={`client-item-${item.id}`}>
      <div className="flex items-start gap-2">
        <span>{m.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{item.title}</p>
          <p className="text-xs text-neutral-400">{contentTypeLabel(item.type)}{item.quantity ? ` · ${item.quantity} attendu(s)` : ""}</p>
          {item.files?.length > 0 && <p className="text-xs text-green-600 mt-0.5">{item.files.length} fichier(s) reçu(s)</p>}
          {item.text && <p className="text-xs text-green-600 mt-0.5">Texte reçu</p>}
          {item.links?.length > 0 && <p className="text-xs text-green-600 mt-0.5">{item.links.length} lien(s) reçu(s)</p>}
        </div>
        {request.status !== "valide" && (
          <Button size="sm" variant="outline" data-testid={`client-deliver-toggle-${item.id}`}
            onClick={() => setOpen(!open)} className="shrink-0 text-xs h-7">
            {open ? "Fermer" : "Envoyer"}
          </Button>
        )}
      </div>

      {open && (
        <div className="mt-3 space-y-2 animate-fade-up">
          <textarea data-testid={`client-item-text-${item.id}`} rows={2} value={text} onChange={(e) => setText(e.target.value)}
            placeholder="Votre texte (prix, horaires, informations…)"
            className="w-full text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white" />
          <div className="flex gap-2">
            <input data-testid={`client-item-link-${item.id}`} value={link} onChange={(e) => setLink(e.target.value)}
              placeholder="https://…" className="flex-1 text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white" />
            <Button size="sm" variant="outline" data-testid={`client-item-addlink-${item.id}`}
              onClick={() => { if (link.trim()) { setLinks((l) => [...l, link.trim()]); setLink(""); } }}>
              Ajouter
            </Button>
          </div>
          {links.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {links.map((l, i) => (
                <span key={i} className="text-[10px] bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 max-w-full truncate">{l}</span>
              ))}
            </div>
          )}
          <input ref={fileInput} type="file" multiple accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.zip"
            className="hidden" data-testid={`client-item-file-${item.id}`}
            onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }} />
          <div
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); pickFiles(e.dataTransfer.files); }}
            data-testid={`client-item-dropzone-${item.id}`}
            className="border-2 border-dashed border-neutral-300 rounded-lg p-4 text-center text-sm text-neutral-500 hover:border-neutral-500 hover:bg-neutral-50 transition-colors cursor-pointer">
            <Paperclip className="w-4 h-4 mx-auto mb-1 text-neutral-400" />
            Glissez vos fichiers ici ou touchez pour choisir (photothèque, caméra, fichiers)
          </div>
          {Object.entries(uploading).map(([name, pct]) => (
            <div key={name} className="text-xs" data-testid={`client-upload-progress-${item.id}`}>
              <p className="text-neutral-500 mb-0.5">Envoi de {name} — {pct} % — ne fermez pas la page</p>
              <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                <div className="h-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          ))}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {files.map((f, i) => (
                <span key={i} className="text-[10px] bg-green-50 text-green-700 rounded px-1.5 py-0.5">{f.name} ✓</span>
              ))}
            </div>
          )}
          <Button data-testid={`client-item-submit-${item.id}`} onClick={submit} disabled={sending || isUploading}
            className="w-full bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
            <Send className="w-3.5 h-3.5" /> {sending ? "Envoi…" : isUploading ? "Envoi de fichier en cours…" : "Envoyer ce contenu"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ClientContentRequests({ token, requests, onChanged }) {
  const [commentFor, setCommentFor] = useState(null);
  const [comment, setComment] = useState("");

  const sendComment = async (r) => {
    if (!comment.trim()) return;
    try {
      await api.post(`/review/${token}/content-requests/${r.id}/comment`, { text: comment.trim() });
      toast.success("Commentaire envoyé");
      setComment(""); setCommentFor(null);
      onChanged();
    } catch { toast.error("Erreur lors de l'envoi"); }
  };

  if (!requests.length) {
    return <p className="text-sm text-neutral-400 text-center py-10" data-testid="client-cr-empty">Aucune demande de contenu pour le moment.</p>;
  }

  return (
    <div className="space-y-4" data-testid="client-cr-section">
      {requests.map((r) => {
        const st = contentRequestStatus(r.status);
        return (
          <div key={r.id} className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5" data-testid={`client-cr-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-heading font-bold flex-1 min-w-0">{r.title}</h3>
              <span className={`text-xs font-medium border rounded-full px-2.5 py-1 ${st.cls}`}>{st.label}</span>
            </div>
            <p className="text-xs text-neutral-400 mt-1">À transmettre avant le {frDate(r.deadline)}</p>
            {r.description && <p className="text-sm text-neutral-600 mt-2">{r.description}</p>}
            <p className="text-sm font-medium mt-3">{r.received_count} / {r.items_count} éléments transmis</p>
            <div className="mt-2 space-y-2">
              {(r.items || []).map((it) => (
                <ItemDelivery key={it.id} token={token} request={r} item={it} onChanged={onChanged} />
              ))}
            </div>
            {(r.comments || []).length > 0 && (
              <div className="mt-3 bg-neutral-50 rounded-lg p-3 space-y-1">
                {r.comments.map((c) => (
                  <p key={c.id} className="text-xs text-neutral-600"><strong>{c.author}</strong> — {c.text}</p>
                ))}
              </div>
            )}
            {commentFor === r.id ? (
              <div className="mt-3 flex gap-2">
                <input data-testid={`client-cr-comment-input-${r.id}`} value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="Votre commentaire…" className="flex-1 text-sm border border-neutral-200 rounded-md px-2.5 py-2 bg-white" />
                <Button size="sm" data-testid={`client-cr-comment-send-${r.id}`} onClick={() => sendComment(r)}
                  className="bg-neutral-900 hover:bg-neutral-800 text-white">Envoyer</Button>
              </div>
            ) : (
              <button className="mt-3 text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors"
                data-testid={`client-cr-comment-btn-${r.id}`} onClick={() => setCommentFor(r.id)}>
                <MessageSquare className="w-3.5 h-3.5" /> Ajouter un commentaire
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
